<template>
  <div v-if="dataDesigner" id="LDSection" class="flex flex-column">
    <section class="pa-12 bd-1 bd-gray-300-brand br-8 bg-white mb-8">
      <div class="flex justify-between items-center">
        <div class="flex items-center">
          <div class="avatar relative w-44 h-44 mr-12">
            <div class="el-cover br-p-50 overflow-hidden">
              <router-link
                :to="dataDesigner.profile ? { name: 'local-designer', params: { name: dataDesigner.profile } } : '#'"
                @click="setLDProfileHeapSource"
              >
                <BaseResponsiveImages v-if="dataDesigner.avatar" :src="dataDesigner.avatar" :alt="dataDesigner.name" />
              </router-link>
            </div>
            <div v-if="isOnline" class="dot absolute w-10 h-10 br-999 bd-1 bd-white bottom-0 right-0 bg-brand-800"></div>
          </div>
          <div class="flex flex-column pt-5">
            <div class="flex items-center">
              <span class="text-lg-sb mb-text-md-sb gray-950">
                <router-link
                  :to="dataDesigner.profile ? { name: 'local-designer', params: { name: dataDesigner.profile } } : '#'"
                  @click="setLDProfileHeapSource"
                >
                  <span class="gray-950">{{ dataDesigner.name }}</span>
                </router-link>
              </span>
              <DJIcon v-if="dataDesigner.is_verified !== 0" color="#2D6CFF" class="ml-4">
                <IconSealCheckFill />
              </DJIcon>
            </div>
            <div class="flex items-center">
              <DJIcon color="#F79009" class="mr-4">
                <IconStarFill />
              </DJIcon>
              <span class="text-md-sb mb-text-sm-sb gray-950">{{ dataDesigner.rating }} </span>
              <span v-if="dataDesigner.number_of_reviews > 0" class="hand" @click.prevent="scrollToReview">
                <span class="ml-4 mr-4 gray-950">•</span>
                <span class="text-md-rg mb-text-sm-rg gray-dark underline mr-4">
                  {{ `(${dataDesigner.number_of_reviews} ${dataDesigner.number_of_reviews === 1 ? 'review' : 'reviews'})` }}
                </span>
              </span>
            </div>
          </div>
        </div>
        <client-only>
          <ChatButtonAuth v-if="dataDesigner.id" type="enquiry-3w" :expertID="designerInfo.id" :tripID="dataTrip.id" :enquiryID="dataTrip.enquiry_id" :avatarLD='designerInfo?.avatar'>
            <DJIcon :size="24">
              <IconMessageMultiple />
            </DJIcon>
          </ChatButtonAuth>
          <template #fallback>
            <DJIcon :size="24">
              <IconMessageMultiple />
            </DJIcon>
          </template>
        </client-only>
      </div>
    </section>
    <template v-if="!isEmpty(dataDesigner.member_of)">
      <span class="block text-md-rg mb-text-sm-rg gray-700">Member of</span>
      <div class="mb-8">
        <div class="dib" v-for="(item, index) in dataDesigner.member_of" :key="index">
          <img :src="item.logo.url" :alt="item.name" class="mr-10" height="24" />
        </div>
      </div>
    </template>
    <client-only>
      <LDOnlineStatus v-show="false" ref="onlineStatusRef" :expertID="dataDesigner.id" />
    </client-only>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue"
import { useRouter } from 'vue-router'
import BaseResponsiveImages from '~/components/ui/BaseResponsiveImages/BaseResponsiveImages.vue'
import LDOnlineStatus from '~/components/ui/chat/LDOnlineStatus.vue'
import ChatButtonAuth from '~/components/ui/chat/ChatButtonAuth.vue'
import DJIcon from '~/components/ui/icon/Icon.vue'
import { IconMessageMultiple, IconSealCheckFill, IconStarFill } from '~/components/ui/icon/icons'
import { isEmpty } from '~/helper'
import { debounceAmplitudeScriptTracking } from '~/mixins/amplitude'
import { debounceHeapTracking } from '~/mixins/heap'
import { useTripPlanStore } from '~/store/pinia/tripPlan'
import { useHeapStore } from '~/store/pinia/heap'

interface Props {
  designerId: number
  designerInfo: any
}
const props = withDefaults(defineProps<Props>(), {
  designerId: 0,
  designerInfo: null,
})
const emits = defineEmits(['chat'])

const router = useRouter()
const dataDesigner = ref<any>(null)
const onlineStatusRef = ref<any>(null)
const tripPlanStore = useTripPlanStore()
const heapStore = useHeapStore()

const dataTrip = computed(() => tripPlanStore.dataTrip)
const isOnline = computed(() => {
  if (onlineStatusRef.value) {
    return !!onlineStatusRef.value.status
  }
  return false
})

async function setLDProfileHeapSource() {
  await heapStore.changeLDProfileHeap('trip-plan-ld-box')
}

function heapTrack() {
  debounceHeapTracking('View Local Designer Reviews', {
    source_widget: 'trip-plan-ld-box',
  })
  debounceAmplitudeScriptTracking('View Local Designer Reviews', {
    source_widget: 'trip-plan-ld-box',
  })
}
function scrollToReview() {
  const offset = document.getElementById('detailed-review')?.offsetTop
  if (offset) {
    window.scrollTo({ top: offset - 100, behavior: 'smooth' })
  } else {
    if (props.designerInfo.profile) {
      setLDProfileHeapSource()
      router.push({
        name: 'local-designer',
        params: { name: props.designerInfo.profile },
        hash: `#reviews`,
      })
    }
  }
  heapTrack()
}

if (props.designerInfo) {
  dataDesigner.value = props.designerInfo
}
</script>

<style lang="scss" scoped></style>
